import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../common/library/Button/Button';
import { TestIds } from '../../assets/testIds';
import { CoachConversationSummary } from '../../api/coachAPI';
import { CoachHistoryStatus } from '../../common/customHooks/useAgentStream';
import { ChatHistoryList } from './ChatHistoryList';

interface ChatHistoryProps {
  conversations: CoachConversationSummary[];
  historyStatus: CoachHistoryStatus;
  // Id of the chat currently on screen, highlighted in the list.
  activeConversationId: string;
  /**
   * True while the on-screen chat is a brand new one the gateway has not seen
   * yet. It is shown as a pending "New chat" entry at the top of the list, the
   * way it appears in the design, and disables the New Chat button.
   */
  isNewChat: boolean;
  onNewChat: () => void;
  onSelectConversation: (conversationId: string) => void;
  onRetry: () => void;
  onRenameConversation: (conversationId: string, title: string) => Promise<void>;
  onDeleteConversation: (conversationId: string) => Promise<void>;
}

// Floor for the measured cap, so the panel is never squeezed to nothing on a
// short window - it scrolls internally instead.
const MIN_PANEL_HEIGHT_PX = 320;
// Breathing room the design leaves between the bottom of the panel and the fold.
const VIEWPORT_BOTTOM_GAP_PX = 24;

/**
 * Caps an element at the room between its own top and the bottom of the
 * viewport, so the panel grows into whatever space the page has and stops at the
 * fold rather than running past it (SN-988 mockups #1 and #2).
 *
 * The top is taken from the element's place in the document rather than its
 * current position on screen, so the cap is a fixed height for a given layout
 * and does not creep upwards as the member scrolls the page.
 */
function useCapToViewportBottom() {
  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }

    const measure = () => {
      const documentTop = element.getBoundingClientRect().top + window.scrollY;
      setMaxHeight(Math.max(
        window.innerHeight - documentTop - VIEWPORT_BOTTOM_GAP_PX,
        MIN_PANEL_HEIGHT_PX,
      ));
    };

    measure();
    window.addEventListener('resize', measure);

    // The panel's top moves whenever anything above it reflows - the welcome
    // banner appearing, the tabs wrapping - which no resize event covers.
    // Re-measuring cannot loop: capping the panel does not move its own top, so
    // the second measurement is the same number and React drops the update.
    const observer = typeof ResizeObserver === 'undefined' ?
      undefined :
      new ResizeObserver(measure);
    observer?.observe(document.body);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  return { ref, maxHeight };
}

/**
 * The Chat History panel beside the AI Business Coach chat: the member's past
 * conversations grouped by when they were last active, plus a New Chat action.
 *
 * The heading and the New Chat button sit outside the scrolling area, so they
 * stay in place while a long list scrolls behind them - the sticky behaviour the
 * design asks for, without `position: sticky`.
 *
 * On small screens Coach.tsx shows ChatHistoryDialog instead of this panel.
 */
export function ChatHistory({
  conversations,
  historyStatus,
  activeConversationId,
  isNewChat,
  onNewChat,
  onSelectConversation,
  onRetry,
  onRenameConversation,
  onDeleteConversation,
}: ChatHistoryProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { ref, maxHeight } = useCapToViewportBottom();

  return (
    <Box
      ref={ref}
      data-testid={TestIds.coach.history.container}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        width: '100%',
        // The panel takes as much of the page as it needs and stops at the fold:
        // its height is its content's, capped at the room between its top and the
        // bottom of the viewport. It is deliberately not tied to the height of
        // the chat beside it - the design lets the panel run well below the chat
        // cards, with the leftover space beside them left blank. Past the cap the
        // list scrolls inside the panel rather than lengthening the page.
        maxHeight: maxHeight ? `${maxHeight}px` : undefined,
        minHeight: 0,
        // The scrolling happens in the list below, not here, so the heading and
        // the buttons around it cannot scroll out of view.
        overflow: 'hidden',
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.divider}`,
        // MUI elevation/1, as used by the panel in the design.
        boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), ' +
          '0px 1px 1px 0px rgba(0, 0, 0, 0.14), ' +
          '0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography
          variant="bodyLgProminent"
          data-testid={TestIds.coach.history.heading}
          sx={{
            display: 'block',
            padding: theme.spacing(2),
            color: theme.palette.text.primary,
          }}
        >
          {t('coach.history.title')}
        </Typography>
        <Divider />

        {/* New Chat is there the whole time, loading list or not: starting a chat
            does not depend on the past ones having arrived (SN-988 mockup #5). */}
        <Box sx={{ padding: theme.spacing(2) }}>
          <OptimusButton
            data-testid={TestIds.coach.history.newChatButton}
            customVariant="secondaryGray"
            buttonText={`${t('coach.history.newChat')}`}
            // The design's icon is the filled Material "chat" bubble (with the
            // message lines), not the empty outlined bubble.
            leadingIcon={<ChatIcon fontSize="small" />}
            onClick={onNewChat}
            disabled={isNewChat}
            size="s"
            // The design outlines this button in the brand colour rather than the
            // variant's grey. `secondaryColor` would be unreadable here - some
            // orgs' brand.primary and brand.secondary are near-identical shades.
            sx={{
              color: theme.palette.brand.primary,
              borderColor: theme.palette.brand.primary,
            }}
          />
        </Box>
      </Box>

      <Box
        data-testid={TestIds.coach.history.scrollArea}
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          // Styling the scrollbar swaps the platform's overlay scrollbar - which
          // fades out and leaves members with no hint the list continues below the
          // fold - for a classic one that stays on screen the whole time the list
          // overflows. `overflow-y: auto` still decides whether there is a
          // scrollbar at all, so a list that fits the panel shows none.
          //
          // Deliberately no `scrollbar-width`/`scrollbar-color`: from Chrome 121
          // the standard properties take precedence over these pseudo-elements
          // and hand the panel back the auto-hiding overlay scrollbar.
          '&::-webkit-scrollbar': {
            width: theme.spacing(1),
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[200],
            borderRadius: theme.spacing(0.5),
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: theme.spacing(0.5),
            '&:hover': {
              backgroundColor: theme.palette.grey[500],
            },
          },
        }}
      >
        <ChatHistoryList
          conversations={conversations}
          historyStatus={historyStatus}
          activeConversationId={activeConversationId}
          isNewChat={isNewChat}
          onSelectConversation={onSelectConversation}
          onRetry={onRetry}
          onRenameConversation={onRenameConversation}
          onDeleteConversation={onDeleteConversation}
        />
      </Box>

    </Box>
  );
}
